import { ExpandMore } from '@mui/icons-material'
import Box from '@mui/material/Box'
import MuiButton from '@mui/material/Button'
import InputBase from '@mui/material/InputBase'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import cls from 'classnames'
import Image from 'next/image'
import { memo, useRef } from 'react'

import Wallet from '@/assets/svgs/wallet.svg'
import Button from '@/components/base/Button'
import useTrans from '@/hooks/useTrans'
import { formatSafe } from '@/utils/numeric'

import BaseAmountInput from '../../BaseAmountInput'
import SelectTokenItem from './SelectTokenItem'
import { selectStyles } from './styles'
import { WithdrawFormViewProps } from './types'
import WithdrawalFormSuccess from './WithdrawalFormSuccess'

const View = ({
  tokenOptions,
  selectedNetwork,
  onChangeToken,
  isNetworkMenuOpened,
  setIsNetworkMenuOpened,
  networkOptions,
  onChangeNetwork,
  toWalletAddress,
  setToWalletAddress,
  onInputAmountChange,
  selectedToken,
  inputAmount,
  isSubmitSucceeded,
  isLoading,
  submitButtonVisibility,
  closeButtonVisibility,
  currentTokenBalance,
  handleInputMaximumBalance,
  handleSubmit,
}: WithdrawFormViewProps) => {
  const trans = useTrans()
  const anchorElRef = useRef<HTMLButtonElement>(null)
  const isShowMenuNetwork = !isLoading && networkOptions.length > 0

  if (isSubmitSucceeded) {
    return (
      <WithdrawalFormSuccess
        inputAmount={inputAmount}
        decimals={selectedToken.decimals}
        tokenName={String(selectedToken.displaySymbol)}
        closeButtonVisibility={closeButtonVisibility}
      />
    )
  }

  return (
    <Stack>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography className="text-sm" data-testid="assets-withdraw-desc-from-wallet">
          {trans.asset_withdrawal.you_withdrawing}
        </Typography>
        <Select
          variant="standard"
          sx={selectStyles}
          IconComponent={ExpandMore}
          value={selectedToken.symbol}
          onChange={onChangeToken}
          className="rounded-[80px] overflow-hidden"
          renderValue={(name) => (
            <SelectTokenItem token={tokenOptions.find(token => token.symbol === name)} />
          )}
          data-testid="assets-withdraw-select-coin"
          disabled={isLoading}
        >
          {tokenOptions.map((token) => (
            <MenuItem key={token.symbol} value={token.symbol} className="p-0">
              <SelectTokenItem token={token} />
            </MenuItem>
          ))}
        </Select>
      </Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" className="mt-2 mb-4">
        <Typography className="text-sm">{trans.asset_withdrawal.through_network}</Typography>
        <MuiButton
          ref={anchorElRef}
          onClick={() => isShowMenuNetwork && setIsNetworkMenuOpened(true)}
          className={cls('cursor-pointer normal-case rounded-[8px] pl-3 pr-1', {
            'bg-gradient-btn': !selectedNetwork,
          })}
          data-testid="assets-withdraw-btn-select-network"
          disabled={isLoading}
        >
          {selectedNetwork ? (
            <Typography className="text-sm font-bold">{selectedNetwork.chain_name_abbr}</Typography>
          ) : (
            <Typography className="text-sm">{trans.asset_withdrawal.select_network}</Typography>
          )}
          {isShowMenuNetwork && <ExpandMore />}
        </MuiButton>
        {
          isShowMenuNetwork && (
            <Menu anchorEl={anchorElRef.current} open={isNetworkMenuOpened} onClose={() => setIsNetworkMenuOpened(false)}>
              {networkOptions.map((network) => (
                <MenuItem
                  key={network.chain_name}
                  value={network.chain_name}
                  className="p-0"
                  onClick={() => onChangeNetwork(network)}
                  data-testid={`assets-withdraw-network-item-${network.chain_name_abbr}`}
                >
                  <Typography className="px-3 py-2 text-sm font-bold" data-testid={`assets-withdraw-network-${network.chain_name_abbr}`}>
                    {network.chain_name_abbr}
                  </Typography>
                </MenuItem>
              ))}
            </Menu>
          )
        }
      </Box>
      {selectedNetwork && (<>
        <Typography className="text-sm">{trans.asset_withdrawal.withdraw_to_wallet_address}</Typography>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          className="rounded-[8px] border border-[#3A3F5D] border-solid bg-[#060814] p-1 min-h-[44px] mt-2"
        >
          <InputBase
            data-testid="assets-withdraw-wallet-address"
            id="assets-withdraw-wallet-address-input"
            title=''
            placeholder={trans.asset_withdrawal.wallet_address(selectedToken.displaySymbol ?? '')}
            value={toWalletAddress}
            onChange={(v) => setToWalletAddress(v.target.value)}
            required
            fullWidth
            inputProps={{
              'aria-label': '0',
              className: 'p-1 text-sm',
            }}
            disabled={isLoading}
          />
        </Box>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          className="mt-2"
          data-testid="assets-withdraw-btn-maximum-balance"
          onClick={handleInputMaximumBalance}
        >
          <Typography className="text-sm">{trans.asset_withdrawal.amount}</Typography>
          <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
            className="space-x-1 cursor-pointer"
          >
            <Image src={Wallet} alt="wallet" width={16} height={16} />
            <Typography
              data-testid="assets-withdraw-token-balance"
              variant="caption"
              className="text-sm text-right opacity-40"
            >
              {formatSafe(currentTokenBalance, selectedToken.decimals)}
            </Typography>
            <Typography className="text-sm underline cursor-pointer opacity-80 hover:opacity-100">
              {`${trans.common.max}`}
            </Typography>
          </Stack>
        </Stack>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          className="rounded-[8px] border border-[#3A3F5D] border-solid bg-[#060814] p-1 min-h-[44px] mt-1"
        >
          <BaseAmountInput
            data-testid="assets-withdraw-amount"
            id="assets-withdraw-amount-input"
            value={inputAmount}
            onChange={onInputAmountChange}
            fullWidth
            decimals={selectedToken?.decimals || 0}
            inputProps={{
              'aria-label': '0',
              className: 'p-1 text-sm',
            }}
            disabled={isLoading}
          />
          <Typography className="mr-1 text-sm">{selectedToken.displaySymbol}</Typography>
        </Box>
        <Button
          onClick={handleSubmit}
          loading={isLoading}
          data-testid="assets-withdraw-btn-submit"
          className="mt-5"
          size="xl"
          disabled={submitButtonVisibility.disabled}
        >
          {submitButtonVisibility.title}
        </Button>
      </>)}
      <Button
        onClick={closeButtonVisibility.click}
        data-testid="assets-withdraw-btn-close"
        disabled={closeButtonVisibility.disabled}
        className="mt-3"
        variant="outlined"
        size="xl"
      >
        {closeButtonVisibility.title}
      </Button>
    </Stack>
  )
}

export default memo(View)
